import axios from "axios";
import { ACCESS_TOKEN_STORAGE_KEY, HTTP_UNAUTHORIZED } from "@/lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const API = `${BACKEND_URL}/api`;

/**
 * JWT format validator — accepts only `xxx.yyy.zzz` (base64url chars).
 * Hardens against rogue browser extensions or malicious code injecting
 * arbitrary strings into localStorage that we'd then forward as a Bearer
 * token. Returns null for any malformed value.
 */
const JWT_RE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
function readToken() {
  try {
    const raw = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    if (!raw || typeof raw !== "string") return null;
    if (raw.length > 4096) return null;
    return JWT_RE.test(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Axios instance for the admin API.
 *
 * Auth strategy: Bearer token via Authorization header.
 * Token is stored in localStorage because the preview edge (Cloudflare)
 * serves CORS preflight with `access-control-allow-origin: *`, which is
 * incompatible with credentialed requests. HttpOnly cookies are also set
 * by the backend as a secondary layer and are used when same-origin.
 *
 * XSS mitigation: the admin bundle does not render any user-supplied HTML;
 * all product/order strings are React-escaped. CSP is enforced via the
 * <meta> tag in index.html. Tokens read from localStorage are validated
 * against a strict JWT shape regex before being forwarded.
 * If the threat model changes (e.g. rich-text editor with HTML), switch
 * to httpOnly cookies only.
 */
export const api = axios.create({
  baseURL: API,
  withCredentials: false,
  headers: { "Content-Type": "application/json" },
});

// Attach token on every request
api.interceptors.request.use((config) => {
  const token = readToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 handler: clear token + redirect to admin login only when user is inside admin area
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err?.response?.status === HTTP_UNAUTHORIZED) {
      const hadToken = !!readToken();
      try { localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY); } catch { /* noop */ }
      // Only redirect if the user is already on an admin page (not the public storefront)
      if (
        typeof window !== "undefined" &&
        hadToken &&
        window.location.pathname.startsWith("/admin") &&
        !window.location.pathname.startsWith("/admin/login")
      ) {
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Произошла ошибка";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join("; ");
  }
  if (detail?.msg) return detail.msg;
  return JSON.stringify(detail);
}
